import {
  BookOpenIcon,
  CameraIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  HelpCircleIcon,
  LayersIcon,
  MapIcon,
  RefreshCwIcon,
  SearchIcon,
  StethoscopeIcon,
  TableIcon,
  TimerIcon,
} from "lucide-react";

export const CHECKOUT_URL = "https://denture-en.impultienda.ar/checkout/";

export const images = {
  product: "https://cdn.magicpatterns.com/uploads/soo7gypipbqCjHLtXmN4CP/DENTURE-EN.png",
  doctor: "https://cdn.magicpatterns.com/uploads/nhDQKaxtdYkCpKVqHY4fCX/DR._NATHAN_BROOKS_-_ORTHO.png",
};

export const beforeItems = [
  "You spend valuable time searching through books and notes.",
  "You're unsure which clinical factors to review first.",
  "You approach different cases without a consistent assessment structure.",
  "You struggle to organize your observations across treatment stages.",
  "You find it difficult to keep your clinical reference materials organized.",
  "You want a more systematic approach to complete denture evaluation.",
];

export const afterItems = [
  "You have six organized reference volumes in one place.",
  "You can consult structured assessment checklists.",
  "You can review key anatomical and prosthetic considerations.",
  "You can document your observations more consistently.",
  "You have visual reference materials for different stages of evaluation.",
  "You can use a repeatable framework to organize your clinical thinking.",
];

export const painPoints = [
  {
    icon: StethoscopeIcon,
    title: "You have a challenging mandibular denture case.",
    text: "You want to evaluate the factors that may be contributing to instability, but you need a clearer way to organize your assessment.",
  },
  {
    icon: TimerIcon,
    title: "You spend too much time looking for clinical information.",
    text: "Your notes, books, and course materials contain valuable information, but finding the relevant section takes longer than it should.",
  },
  {
    icon: HelpCircleIcon,
    title: "You're unsure where to begin your assessment.",
    text: "You want a structured starting point rather than approaching every case without a consistent framework.",
  },
  {
    icon: ClipboardListIcon,
    title: "You want to standardize your clinical workflow.",
    text: "You know the importance of careful evaluation, but you would benefit from practical checklists and organized reference materials.",
  },
  {
    icon: RefreshCwIcon,
    title: "You want to improve your documentation.",
    text: "You need a clearer way to record relevant observations, identify unanswered questions, and organize follow-up.",
  },
  {
    icon: BookOpenIcon,
    title: "You want to strengthen your approach to complete dentures.",
    text: "You want practical reference materials that complement your existing education and clinical experience.",
  },
];

export const volumes = [
  {
    number: "I",
    title: "The Complete Denture Assessment Blueprint",
    subtitle: "Build a Structured Starting Point for Every Case",
    intro: "Before considering individual procedures, you need a clear picture of the case in front of you. In this volume, I help you organize the initial assessment of the edentulous patient and identify the information that deserves attention before moving forward.",
    items: [
      "A structured initial assessment framework.",
      "Key information to document about the patient and clinical situation.",
      "An overview of relevant anatomical and functional considerations.",
      "A checklist for organizing your initial observations.",
      "A framework for identifying unanswered clinical questions.",
      "A visual map of the general assessment process.",
      "A practical case documentation template.",
    ],
    goal: "Give you a more organized starting point for evaluating complete denture cases.",
  },
  {
    number: "II",
    title: "The Edentulous Anatomy Reference Guide",
    subtitle: "Understand the Anatomical Factors That Matter in Complete Denture Assessment",
    intro: "The anatomy of the edentulous patient plays an important role in evaluating support, stability, and retention. This volume brings together visual reference materials to help you review the structures and anatomical characteristics relevant to complete dentures.",
    items: [
      "An overview of relevant maxillary anatomy.",
      "An overview of relevant mandibular anatomy.",
      "Residual ridge characteristics.",
      "General considerations related to supporting tissues.",
      "Anatomical reference maps.",
      "A visual glossary of important terms.",
      "Comparison tables for selected anatomical features.",
      "An anatomical observation checklist.",
    ],
    goal: "Make important anatomical reference information easier to find and review.",
  },
  {
    number: "III",
    title: "The Impression Assessment Playbook",
    subtitle: "Organize Your Review of the Factors That Influence Complete Denture Impressions",
    intro: "Impression procedures require attention to anatomy, tissue conditions, material selection, and technique. I've organized this volume around the information you may need to evaluate and document throughout the impression process.",
    items: [
      "An overview of complete denture impression concepts.",
      "A checklist for recording relevant tissue observations.",
      "General considerations for tray selection and extension.",
      "A framework for documenting impression-related observations.",
      "A checklist for reviewing the resulting model.",
      "A material documentation sheet.",
      "A record for noting procedural issues.",
      "A clinical-laboratory communication template.",
    ],
    goal: "Provide a structured reference for organizing impression-related information and reviewing relevant factors.",
  },
  {
    number: "IV",
    title: "The Stability & Retention Reference System",
    subtitle: "Organize Your Evaluation of the Factors That May Affect Denture Stability",
    intro: "A complete denture's performance depends on multiple anatomical, functional, and prosthetic considerations. In this volume, I bring those considerations together in a visual reference system designed to help you organize your review.",
    items: [
      "A practical overview of support, stability, and retention.",
      "A framework for reviewing mandibular denture stability.",
      "A framework for reviewing maxillary denture retention.",
      "Anatomical and functional factors to consider.",
      "A checklist for documenting observed displacement.",
      "A visual reference for relevant muscular considerations.",
      "A matrix for organizing potential contributing factors.",
      "A template for recording findings and questions requiring further evaluation.",
    ],
    goal: "Help you organize your assessment rather than relying on scattered notes or isolated pieces of information.",
  },
  {
    number: "V",
    title: "The Occlusion & Prosthetic Review Guide",
    subtitle: "Bring Greater Structure to Your Review of Occlusal Concepts",
    intro: "Occlusal considerations are an important part of complete denture treatment planning. This volume organizes fundamental concepts and visual comparisons to make them easier to review when studying or documenting a case.",
    items: [
      "An introduction to complete denture occlusion.",
      "A visual overview of bilateral balanced occlusion.",
      "An introduction to lingualized occlusion.",
      "An overview of monoplane occlusion.",
      "A comparison of selected occlusal concepts.",
      "General considerations related to intermaxillary relationships.",
      "A visual glossary.",
      "A checklist for organizing occlusal observations.",
    ],
    goal: "Give you an organized reference for reviewing key occlusal concepts and documenting relevant considerations.",
  },
  {
    number: "VI",
    title: "The Clinical Documentation & Follow-Up System",
    subtitle: "Organize Your Observations From Initial Assessment Through Follow-Up",
    intro: "Clinical work doesn't end when a procedure is completed. Clear documentation helps you record what you observed, identify outstanding questions, and organize follow-up information. In this final volume, I bring together practical templates designed to support a more consistent documentation process.",
    items: [
      "An initial case documentation form.",
      "A clinical observation worksheet.",
      "A treatment-stage documentation template.",
      "A record for noting complications or unexpected findings.",
      "A pre-delivery review checklist.",
      "A follow-up documentation form.",
      "A template for organizing clinical photographs.",
      "A case review worksheet.",
      "A personal learning and reflection log.",
    ],
    goal: "Make it easier to maintain an organized record of your observations and follow-up activities.",
  },
];

export const libraryItems = [
  { icon: MapIcon, title: "Visual Assessment Maps", text: "Reference maps that help you navigate the main topics covered in the collection." },
  { icon: ClipboardCheckIcon, title: "Professional Checklists", text: "Structured lists to help you organize observations and review relevant information at different stages." },
  { icon: LayersIcon, title: "Anatomical Reference Sheets", text: "Visual resources for reviewing important anatomical concepts related to complete dentures." },
  { icon: TableIcon, title: "Comparison Tables", text: "Organized summaries of selected anatomical, impression-related, and occlusal concepts." },
  { icon: CameraIcon, title: "Clinical Documentation Templates", text: "Reusable forms for recording observations, organizing case information, and documenting follow-up." },
  { icon: SearchIcon, title: "Quick-Reference Pages", text: "Condensed reference materials designed to help you find the relevant section without searching through an entire volume." },
];

export const bonuses = [
  {
    number: 1,
    title: "The Mandibular Denture Assessment Kit",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/3uKFXj3Yz4G8ibMcYdnm4Y/BONUS_1_DENTURE-EN.png",
    description: "A focused collection of reference sheets and checklists for organizing the evaluation of mandibular complete dentures.",
    items: [
      "A mandibular assessment checklist.",
      "An anatomical observation sheet.",
      "A functional considerations worksheet.",
      "A denture displacement documentation form.",
      "A case review template.",
    ],
    note: "Designed to help you organize your observations when reviewing mandibular denture cases.",
  },
  {
    number: 2,
    title: "The Impression Review Checklist Pack",
    value: 15,
    image: "https://cdn.magicpatterns.com/uploads/ayFHue6A4Nqkin41YoWTh8/BONUS_2_DENTURE-EN.png",
    description: "A practical set of checklists for organizing your review of complete denture impressions.",
    items: [
      "An impression assessment checklist.",
      "A tissue observation form.",
      "A tray and material documentation sheet.",
      "A model review checklist.",
      "An impression-related incident log.",
    ],
    note: "Use these resources to document your observations and organize the information relevant to your impression workflow.",
  },
  {
    number: 3,
    title: "The Denture Anatomy Visual Atlas",
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/aWMdxDhqdnmyxwx6cC6umX/BONUS_3_DENTURE-EN.png",
    description: "A visual reference collection covering selected anatomical structures relevant to complete denture assessment.",
    items: [
      "Maxillary anatomical reference sheets.",
      "Mandibular anatomical reference sheets.",
      "A visual glossary.",
      "Anatomical comparison tables.",
      "Printable review pages.",
    ],
    note: "This atlas is designed as a visual learning aid and should be reviewed against authoritative anatomical references.",
  },
  {
    number: 4,
    title: "The Clinical-Laboratory Communication Toolkit",
    value: 15,
    image: "https://cdn.magicpatterns.com/uploads/bYhxLy6m3Z26ze4nr6z6hN/BONUS_4_DENTURE-EN.png",
    description: "A collection of editable templates to help you organize information shared between the clinical practice and the dental laboratory.",
    items: [
      "A case submission form.",
      "A clinical instruction template.",
      "A material documentation sheet.",
      "A modification request form.",
      "An incident documentation template.",
      "A case handoff checklist.",
    ],
    note: "The goal is to make relevant case information easier to document and communicate.",
  },
  {
    number: 5,
    title: "The Complete Denture Case Workbook",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/huLG7GoHMpRh23XfSoZ4ws/BONUS_5_DENTURE-EN.png",
    description: "A structured workbook for documenting cases, recording observations, and organizing your professional learning.",
    items: [
      "Case documentation pages.",
      "Initial assessment worksheets.",
      "Clinical observation logs.",
      "Follow-up forms.",
      "Case review templates.",
      "A personal learning tracker.",
    ],
    note: "Important: Keep patient information confidential and use the workbook in accordance with applicable privacy requirements.",
  },
];

export const offerChecklist = [
  "Instant Digital Access",
  "Six Professional Reference Volumes",
  "Five Exclusive Bonuses",
  "Lifetime Access to Your Purchased Files",
  "No Monthly Subscription",
  "7-Day Money-Back Guarantee",
];

export const guaranteeItems = [
  "Explore all six volumes.",
  "Review the anatomical reference materials.",
  "Examine the assessment checklists.",
  "Use the documentation templates.",
  "Review the five additional bonuses.",
];

export const testimonials = [
  {
    name: "Dra. Sofía Ramírez",
    role: "General Dentist",
    location: "Guadalajara, Mexico",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/d21af454-4b9b-4a91-a13e-f36dfda8dde9.jpg",
    quote: "I finally have my complete denture notes in one place. The initial assessment checklist is now the first thing I open before a new edentulous patient.",
  },
  {
    name: "Dr. Michael Turner",
    role: "Restorative Dentist",
    location: "Austin, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/f4b6a820-4282-4758-a905-8faba28495a1.jpg",
    quote: "After 25 years in practice I didn't expect much, but the stability and retention matrix gave me a cleaner way to organize what I was already observing.",
  },
  {
    name: "Dr. Angela Brooks-Hill",
    role: "Prosthodontics Resident",
    location: "Atlanta, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/5d352b10-1bfd-44fd-bb2d-51cdff2e9726.jpg",
    quote: "The anatomy reference sheets and occlusion comparisons are excellent for review. Short, visual, and easy to find when I need them.",
  },
  {
    name: "Dr. Daniel Ortega",
    role: "General Dentist",
    location: "Bogotá, Colombia",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/12627764-8343-4994-a805-98ddfa730edf.jpg",
    quote: "The lab communication templates alone were worth it. My case submissions are more consistent and I spend less time rewriting instructions.",
  },
];

export const faqs = [
  { q: "How will I receive Denture Blueprint™?", a: "After your payment is completed, you'll receive instructions for accessing your purchase and downloading the digital materials." },
  { q: "What exactly is included?", a: "Your purchase includes six digital reference volumes and five complementary bonuses covering complete denture assessment, anatomy, impressions, stability and retention, occlusal concepts, documentation, and related topics." },
  { q: "Is this a video course?", a: "No. Denture Blueprint™ is primarily a digital reference collection containing guides, visual resources, checklists, and templates. It is designed for professionals who prefer organized written and visual materials." },
  { q: "Is the content suitable for dentists in different countries?", a: "The collection addresses general educational concepts related to complete dentures. However, clinical practice requirements, professional regulations, available materials, and accepted procedures can vary by country. Always follow applicable local requirements and professional standards." },
  { q: "Do I need to be a prosthodontist?", a: "The collection is intended as a reference resource for dental professionals interested in complete denture assessment. Your individual scope of practice, training, and experience determine which procedures you may appropriately perform." },
  { q: "Will this replace my clinical training?", a: "No. Denture Blueprint™ is an educational reference collection. It does not replace formal education, supervised clinical training, individualized patient assessment, or professional judgment." },
  { q: "Can I download and keep the files?", a: "Yes. Your purchase includes access to the digital files for download and personal professional reference, subject to the applicable license terms." },
  { q: "Is this a recurring payment?", a: "No. The advertised price is a one-time payment of $27, with no monthly subscription." },
  { q: "How does the 7-day guarantee work?", a: "If you're not satisfied with your purchase, contact us within seven days of purchase to request a refund under the guarantee terms." },
  { q: "Will this guarantee better clinical outcomes?", a: "No educational reference system can guarantee a particular clinical result. Denture Blueprint™ is designed to help organize relevant information and documentation. Clinical outcomes depend on multiple factors, including patient characteristics, professional training, clinical judgment, and execution." },
  { q: "Is patient information included in the templates?", a: "The templates are designed for your own documentation. They do not provide patient records or real patient information. Always protect confidential information and comply with applicable privacy requirements." },
  { q: "What if I have questions about my purchase?", a: "Contact our customer support team using the contact details provided during checkout or in your purchase confirmation." },
];

export const recentBuyers = [
  { name: "Valentina R.", country: "Colombia" },
  { name: "Dr. Alejandro M.", country: "Mexico" },
  { name: "Camila S.", country: "Argentina" },
  { name: "Dr. Javier P.", country: "Spain" },
  { name: "Lucía F.", country: "Chile" },
  { name: "Dr. Andrés G.", country: "Peru" },
  { name: "María José C.", country: "Ecuador" },
  { name: "Dr. Diego H.", country: "Uruguay" },
  { name: "Daniela V.", country: "Costa Rica" },
  { name: "Dr. Fernando L.", country: "Dominican Republic" },
  { name: "Gabriela T.", country: "Guatemala" },
  { name: "Dr. Ricardo A.", country: "Paraguay" },
  { name: "Isabel N.", country: "Panama" },
  { name: "Dr. Sebastián Q.", country: "Bolivia" },
  { name: "Mariana O.", country: "Puerto Rico" },
];
